import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import AdminLayout from '../components/AdminLayout';
import { contactSubjectsAPI } from '../api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Plus, Edit2, Trash2, GripVertical } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const AdminContactSubjects = () => {
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSubject, setEditingSubject] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    order: 0,
    is_active: true,
  });
  const { toast } = useToast();

  useEffect(() => {
    loadSubjects();
  }, []);

  const loadSubjects = async () => {
    try {
      const response = await contactSubjectsAPI.getAll();
      setSubjects(response.data);
    } catch (error) {
      console.error('Error loading subjects:', error);
      toast({
        title: 'Hata',
        description: 'Konular yüklenirken bir hata oluştu.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingSubject) {
        await contactSubjectsAPI.update(editingSubject._id, formData);
        toast({
          title: 'Başarılı',
          description: 'Konu güncellendi.',
        });
      } else {
        await contactSubjectsAPI.create(formData);
        toast({
          title: 'Başarılı',
          description: 'Konu eklendi.',
        });
      }
      
      resetForm();
      loadSubjects();
    } catch (error) {
      console.error('Error saving subject:', error);
      toast({
        title: 'Hata',
        description: 'Konu kaydedilirken bir hata oluştu.',
        variant: 'destructive',
      });
    }
  };

  const handleEdit = (subject) => {
    setEditingSubject(subject);
    setFormData({
      name: subject.name,
      order: subject.order,
      is_active: subject.is_active,
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu konuyu silmek istediğinizden emin misiniz?')) return;
    
    try {
      await contactSubjectsAPI.delete(id);
      toast({
        title: 'Başarılı',
        description: 'Konu silindi.',
      });
      loadSubjects();
    } catch (error) {
      console.error('Error deleting subject:', error);
      toast({
        title: 'Hata',
        description: 'Konu silinirken bir hata oluştu.',
        variant: 'destructive',
      });
    }
  };

  const resetForm = () => {
    setEditingSubject(null);
    setFormData({
      name: '',
      order: 0,
      is_active: true,
    });
    setShowForm(false);
  };

  return (
    <AdminLayout>
      <Helmet>
        <title>İletişim Konuları - Cinefora Admin</title>
      </Helmet>

      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white">İletişim Konuları</h1>
          <p className="text-gray-400 mt-2">İletişim formunda gösterilecek konu seçeneklerini yönetin</p>
        </div>
        <Button
          onClick={() => setShowForm(!showForm)}
          className="bg-[#FDB714] hover:bg-[#e5a510] text-black"
        >
          <Plus className="mr-2" size={20} />
          Yeni Konu Ekle
        </Button>
      </div>

      {showForm && (
        <Card className="bg-gray-900 border-gray-800 p-6 mb-6">
          <h2 className="text-xl font-bold text-white mb-4">
            {editingSubject ? 'Konuyu Düzenle' : 'Yeni Konu Ekle'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-white block mb-2">Konu Adı *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
                placeholder="Örn: Genel Bilgi"
                required
              />
            </div>

            <div>
              <label className="text-white block mb-2">Sıra *</label>
              <input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
                placeholder="0"
                required
              />
              <p className="text-gray-500 text-sm mt-1">Küçük sayılar önce gösterilir</p>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-4 h-4 cursor-pointer"
              />
              <label htmlFor="is_active" className="text-white cursor-pointer">
                Aktif (İletişim formunda göster)
              </label>
            </div>

            <div className="flex gap-3">
              <Button type="submit" className="bg-[#FDB714] hover:bg-[#e5a510] text-black">
                {editingSubject ? 'Güncelle' : 'Ekle'}
              </Button>
              <Button type="button" onClick={resetForm} className="bg-gray-700 hover:bg-gray-600 text-white">
                İptal
              </Button>
            </div>
          </form>
        </Card>
      )}

      {loading ? (
        <p className="text-white text-center">Yükleniyor...</p>
      ) : subjects.length === 0 ? (
        <Card className="bg-gray-900 border-gray-800 p-8 text-center">
          <p className="text-gray-400">Henüz konu eklenmemiş.</p>
        </Card>
      ) : (
        <Card className="bg-gray-900 border-gray-800 p-6">
          <div className="space-y-3">
            {subjects.map((subject) => (
              <div
                key={subject._id}
                className="bg-gray-800 rounded-lg p-4 flex items-center justify-between hover:bg-gray-750 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <GripVertical className="text-gray-600" size={20} />
                  <div>
                    <h3 className="text-white font-semibold">{subject.name}</h3>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-gray-500 text-sm">Sıra: {subject.order}</span>
                      <span className={`text-xs px-2 py-1 rounded ${
                        subject.is_active 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-red-500/20 text-red-400'
                      }`}>
                        {subject.is_active ? 'Aktif' : 'Pasif'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => handleEdit(subject)}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <Edit2 size={16} />
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleDelete(subject._id)}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </AdminLayout>
  );
};

export default AdminContactSubjects;
